<script setup lang="ts">
import { computed, ref, toRefs } from "vue";
import { z } from "zod";

import FavIcon from "@/components/FavIcon.vue";
import MapInfoButton from "@/components/MapInfoButton.vue";
import MapInfoModal from "@/components/MapInfoModal.vue";
import OrganisationSelectorModal from "@/components/OrganisationSelectorModal.vue";
import OrganisationsMap from "@/components/OrganisationsMap.vue";
import OrganisationsMapFilter, { type FilterCriteria } from "@/components/OrganisationsMapFilter.vue";
import OrganisationStatisticsModal from "@/components/OrganisationStatisticsModal.vue";
import OrganisationTypeIcon from "@/components/OrganisationTypeIcon.vue";
import SidebarFilter from "@/components/SidebarFilter.vue";
import SidebarFilterButton from "@/components/SidebarFilterButton.vue";
import { useValidatedProps } from "@/composable";
import {
  mapInfoSchema,
  type Organisation,
  organisationTypes,
  organisationWithQuestionnaireIdsSchema,
  questionnaireSchema
} from "@/types";

const { questionnaires, organisations, mapInfo } = toRefs(
  useValidatedProps(
    z.object({
      questionnaires: z.array(questionnaireSchema),
      organisations: z.array(organisationWithQuestionnaireIdsSchema),
      mapInfo: mapInfoSchema
    })
  )
);

const filterCriteria = ref<FilterCriteria>({
  organisationType: undefined,
  questionnaireId: undefined
});

const filteredOrganisations = computed(() => {
  const { organisationType, questionnaireId } = filterCriteria.value;

  return organisations.value.filter((organisation) => {
    if (organisationType !== undefined && organisation.type !== organisationType) {
      return false;
    }

    if (questionnaireId !== undefined && !organisation.questionnaireIds.includes(questionnaireId)) {
      return false;
    }

    return true;
  });
});

const selectedOrganisation = ref<Organisation | Organisation[]>();
const showInfoModal = ref(false);
</script>

<template>
  <div class="d-flex dvh-100">
    <main class="flex-grow-1">
      <SidebarFilterButton class="position-absolute top-0 end-0 z-1 m-2" />

      <OrganisationsMap
        :organisations="filteredOrganisations"
        class="w-100 h-100"
        @select="(organisation) => (selectedOrganisation = organisation)"
      />
      <OrganisationSelectorModal
        v-if="Array.isArray(selectedOrganisation)"
        :organisations="selectedOrganisation"
        @did-hide="selectedOrganisation = undefined"
        @select="(organisation) => (selectedOrganisation = organisation)"
      />
      <OrganisationStatisticsModal
        v-else-if="selectedOrganisation"
        :organisation="selectedOrganisation"
        @did-hide="selectedOrganisation = undefined"
      />
      <MapInfoModal v-if="showInfoModal" :map-info="mapInfo" @did-hide="showInfoModal = false" />
    </main>
    <SidebarFilter>
      <template #title><FavIcon /> PRO-landkort</template>
      <p>
        PRO-landkort viser anvendelsen af PRO spørgeskemaer over hele landets speciallæge- og lægepraksis, kommuner og
        hospitaler. Når man klikker på de enkelte lokationer
        <span class="text-nowrap">
          <OrganisationTypeIcon v-for="type in organisationTypes" :key="type" class="flex-shrink-0" :type />
        </span>
        kan man se grafer for udviklingen af anvendelsen af PRO spørgeskemaer, fordelt på måneder.
      </p>
      <p>
        Det er en forudsætning, at det enkelte PRO-skema datadeles via KIH Databasen, for at det fremgår af
        PRO-landkort.
      </p>
      <OrganisationsMapFilter v-model="filterCriteria" :questionnaires="questionnaires" />

      <div class="mt-3">
        <MapInfoButton @click="showInfoModal = true" />
      </div>
    </SidebarFilter>
  </div>
</template>
